/**
 * Date utility functions for streak calculation and date formatting
 */

/**
 * Format a date as a localized string
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format a date as a short string (e.g., "Feb 14, 2026")
 */
export function formatDateShort(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Check if two dates are on the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if two dates are consecutive days
 */
export function isConsecutiveDay(date1: Date, date2: Date): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 1;
}

/**
 * Calculate streak from an array of session dates
 * Returns the number of consecutive days with at least one session
 */
export function calculateStreak(sessionDates: Date[]): number {
  if (sessionDates.length === 0) return 0;

  // Sort dates in descending order (most recent first)
  const sortedDates = sessionDates
    .map(d => new Date(d))
    .sort((a, b) => b.getTime() - a.getTime());

  // Remove duplicate days
  const uniqueDays: Date[] = [];
  for (const date of sortedDates) {
    if (uniqueDays.length === 0 || !isSameDay(date, uniqueDays[uniqueDays.length - 1])) {
      uniqueDays.push(date);
    }
  }

  if (uniqueDays.length === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if the most recent session was today or yesterday
  const mostRecent = uniqueDays[0];
  if (!mostRecent) return 0;

  mostRecent.setHours(0, 0, 0, 0);

  const daysSinceLastSession = Math.floor(
    (today.getTime() - mostRecent.getTime()) / (1000 * 60 * 60 * 24),
  );

  // If last session was more than 1 day ago, streak is broken
  if (daysSinceLastSession > 1) return 0;

  // Count consecutive days
  let streak = 1;
  for (let i = 1; i < uniqueDays.length; i++) {
    const current = uniqueDays[i];
    const previous = uniqueDays[i - 1];
    if (current && previous && isConsecutiveDay(current, previous)) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

/**
 * Generate an array of dates for the last N weeks
 * Returns dates in chronological order (oldest first)
 */
export function generateDateRange(weeks: number): Date[] {
  const dates: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totalDays = weeks * 7;
  for (let i = totalDays - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(date);
  }

  return dates;
}

/**
 * Get the day of week (0 = Sunday, 6 = Saturday)
 */
export function getDayOfWeek(date: Date): number {
  return date.getDay();
}

/**
 * Format date for heatmap tooltip (e.g., "Feb 14, 2026")
 */
export function formatHeatmapDate(date: Date): string {
  return formatDateShort(date);
}
